/**
 * Wochen- und Monatsplanung (Roadmap F-16) - rein und getestet.
 *
 * Wochen- und Monatsziele sind kurzfristige Fokuspunkte ("Kapitel 3 lernen").
 * Sie sind bewusst KEIN weiterer Zielhorizont: Zieleseite, Deadlines und
 * Uebersicht bleiben so unberuehrt. Optional zahlen sie auf ein grosses Ziel
 * ein (`goalId`).
 */
import { addDays, isoWeekKey } from '@/lib/datetime';
import { monatsBereich, monatVerschieben, montagVonWoche } from '@/domain/review/periods';
import type { CalendarOccurrence, ID, ISOInstant, LocalDate, Project, Task } from '@/domain/types';
import { projectPriority, projectPriorityRank } from '@/domain/priority';

export type FokusArt = 'woche' | 'monat';
export type FokusStatus = 'offen' | 'erledigt' | 'uebernommen' | 'gestrichen';

export interface FocusGoal {
  id: ID;
  art: FokusArt;
  /** `'2026-W40'` bzw. `'2026-10'`. */
  key: string;
  title: string;
  status: FokusStatus;
  /** Grosses Ziel, auf das der Fokuspunkt einzahlt. */
  goalId?: ID;
  order: number;
  /** Aus dem vorigen Zeitraum uebernommen. */
  uebernommenVon?: ID;
  createdAt: ISOInstant;
  updatedAt: ISOInstant;
}

const MUSTER: Record<FokusArt, RegExp> = {
  woche: /^\d{4}-W(0[1-9]|[1-4]\d|5[0-3])$/,
  monat: /^\d{4}-(0[1-9]|1[0-2])$/,
};

export function fokusKeyGueltig(art: FokusArt, key: string): boolean {
  return (art === 'woche' || art === 'monat') && MUSTER[art].test(key);
}

/** Erster und letzter Tag des Zeitraums (inklusiv). */
export function zeitraum(art: FokusArt, key: string): { von: LocalDate; bis: LocalDate } {
  if (art === 'monat') return monatsBereich(key);
  const von = montagVonWoche(key)!;
  return { von, bis: addDays(von, 6) };
}

/** Der Zeitraum davor bzw. danach - `schritt` in Wochen oder Monaten. */
export function nachbarKey(art: FokusArt, key: string, schritt: number): string {
  if (art === 'monat') return monatVerschieben(key, schritt);
  return isoWeekKey(addDays(montagVonWoche(key)!, 7 * schritt));
}

/** Offene Fokuspunkte eines Zeitraums, in ihrer Reihenfolge. */
export function offene(goals: FocusGoal[]): FocusGoal[] {
  return goals.filter((g) => g.status === 'offen').sort((a, b) => a.order - b.order);
}

/** Aufgaben, die im Zeitraum eingeplant waren und nicht erledigt sind. */
export function liegengeblieben(tasks: Task[], von: LocalDate, bis: LocalDate): Task[] {
  return tasks
    .filter((t) => t.status !== 'erledigt' && t.plannedDate && t.plannedDate >= von && t.plannedDate <= bis)
    .sort((a, b) => a.plannedDate!.localeCompare(b.plannedDate!) || a.order - b.order);
}

export interface MonatsProjekt {
  project: Project;
  /** Warum es in diesem Monat wichtig ist - direkt sichtbar (Leitlinie 2). */
  gruende: string[];
}

/**
 * Die wichtigsten Projekte eines Monats: aktive Projekte mit Deadline,
 * Meilenstein, geplanter oder faelliger Aufgabe oder verknuepftem Termin im
 * Monat. Sortiert nach Prioritaet, dann Deadline.
 */
export function projekteDesMonats(
  input: { projects: Project[]; tasks: Task[]; occurrences: CalendarOccurrence[] },
  von: LocalDate,
  bis: LocalDate,
): MonatsProjekt[] {
  const drin = (d?: string) => Boolean(d) && d!.slice(0, 10) >= von && d!.slice(0, 10) <= bis;
  const out: MonatsProjekt[] = [];
  for (const project of input.projects) {
    if (project.status !== 'aktiv') continue;
    const gruende: string[] = [];
    if (drin(project.deadline)) gruende.push('Deadline');
    const meilensteine = (project.milestones ?? []).filter((m) => !m.done && drin(m.date)).length;
    if (meilensteine) gruende.push(meilensteine === 1 ? '1 Meilenstein' : `${meilensteine} Meilensteine`);
    const aufgaben = input.tasks.filter((t) => t.projectId === project.id && t.status !== 'erledigt'
      && (drin(t.plannedDate) || drin(t.dueDate))).length;
    if (aufgaben) gruende.push(aufgaben === 1 ? '1 Aufgabe' : `${aufgaben} Aufgaben`);
    const termine = input.occurrences.filter((o) => o.projectId === project.id && drin(String(o.start))).length;
    if (termine) gruende.push(termine === 1 ? '1 Termin' : `${termine} Termine`);
    if (gruende.length) out.push({ project, gruende });
  }
  return out.sort((a, b) =>
    projectPriorityRank(projectPriority(a.project)) - projectPriorityRank(projectPriority(b.project))
    || (a.project.deadline ?? '9999').localeCompare(b.project.deadline ?? '9999')
    || a.project.title.localeCompare(b.project.title, 'de'));
}
